import React, { useState, useEffect } from "react";
import withRouter from "utils/withRouter";
import SwitchListCommon from "views/Switch/SwitchList/SwitchListCommon";
import { COLUMN } from "../../Switch/SwitchList/CreateColumnListForSwitchList";
import {
  DEFAULT_BULK_ACTION_LIST_SORT,
  DEFAULT_BULK_ACTION_FILTER,
  DEFAULT_PAGE_SIZE,
  DEFAULT_BULK_ACTION_HOLD_FILTER,
  DEFAULT_BULK_ACTION_MISSING_PRESCRIBER_FILTER,
  DEFAULT_BULK_ACTION_REPROCESS_DISPOSITION_ERRORS_FILTER,
  DEFAULT_BULK_ACTION_REPROCESS_WORKFLOW_ERRORS_FILTER,
  hasRestrictedStatusFilter,
  getRestrictedStatusFilter,
  shouldHideDefaultFiltersToggle,
} from "lookup/BulkActionListConstants";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Col,
  Row,
  Form,
  OverlayTrigger,
  Tooltip,
  Container,
} from "react-bootstrap";
import { MdHelp } from "react-icons/md";
import {
  updateInterventionList,
  updateIndexOnList,
  resetError,
  updateBulkActionListState,
} from "./BulkActionList.actions";
import { sendAuthenticatedAsyncRequest } from "services/AsyncRequestService";
import { DEFAULT_BULK_ACTION_CLIENT_PAUSE_FILTER } from "lookup/BulkActionListConstants";
import { FAX_RESEND_BULK_ACTION_TYPE } from "lookup/BulkActionListConstants";
import { isLoggedInUserBioSimilarUser } from "services/AWUtilityService";
import { BULK_ACTION_FILTER } from "lookup/BulkActionListConstants";
import { SWITCH_NUMBER_OP } from "lookup/GlobalConstants";
import { SWITCH_NUMBER_STRING } from "lookup/GlobalConstants";

const columnListWithUuid = [
  COLUMN.customerNameAbbreviation,
  COLUMN.clientNameAbbreviation,
  COLUMN.switchNumber,
  COLUMN.generatedDate,
  COLUMN.projectedSavings,
  COLUMN.state,
  COLUMN.bulkActionStatus,
  COLUMN.bulkActionItemStatus,
];

const columnListWithoutUuid = [
  COLUMN.customerNameAbbreviation,
  COLUMN.clientNameAbbreviation,
  COLUMN.switchNumber,
  COLUMN.generatedDate,
  COLUMN.projectedSavings,
  COLUMN.state,
  COLUMN.bulkActionStatus,
];

const columnListWithoutUuidBasedOnSelectedAction = (bulkActionType) => {
  let columns = [...columnListWithoutUuid];
  if (bulkActionType.value == FAX_RESEND_BULK_ACTION_TYPE) {
    columns.push(COLUMN.isPickupConfirmed);
  }
  return columns;
};

const BulkActionList = (props) => {
  const dispatch = useDispatch();
  const {
    selectedCustomerNameAbbreivation,
    selectedClientNameAbbreviation,
    selected,
    setSelectedSwitches,
    actionType,
    bulkActionUuid,
    foundClientPause,
    bioSimilarSwitchesToggle,
    setBioSimilarSwitchesToggle,
    showBioSimilarToggle,
    isViewable,
    isEditable,
    filters,
  } = props;
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [resetTableSelection, setTableSelection] = useState(false);
  const [toggleAllSwitches, setToggleAllSwitches] = useState(false);
  const [isFetchedOnce, setIsFetchedOnce] = useState(false);
  const [disableTable, setDisableTable] = useState(false);
  const [toggleDefaultFilters, setToggleDefaultFilters] = useState(
    isEditable || isViewable ? false : true
  );
  const filterMap = {
    "Hold Resume": DEFAULT_BULK_ACTION_HOLD_FILTER,
    "Update Status to Delivered": DEFAULT_BULK_ACTION_MISSING_PRESCRIBER_FILTER,
    "Client Pause": DEFAULT_BULK_ACTION_CLIENT_PAUSE_FILTER || [],
    "Reprocess Disposition Errors": DEFAULT_BULK_ACTION_REPROCESS_DISPOSITION_ERRORS_FILTER,
    "Reprocess Workflow Errors": DEFAULT_BULK_ACTION_REPROCESS_WORKFLOW_ERRORS_FILTER,
  };

  const data = useSelector((state) =>
    state.bulkActionList.data !== undefined ? state.bulkActionList.data : []
  );
  const pages = useSelector((state) =>
    state.bulkActionList.pages !== undefined ? state.bulkActionList.pages : 0
  );

  const totalElements = useSelector((state) =>
    state.bulkActionList.totalElements !== undefined
      ? state.bulkActionList.totalElements
      : "Loading ..."
  );

  const loading = useSelector((state) => state.bulkActionList.loading);

  const isError = useSelector((state) => state.bulkActionList.isError);

  const listState = useSelector((state) =>
    state.bulkActionList.listState !== undefined
      ? state.bulkActionList.listState
      : {
          page: 0,
          pageSize: DEFAULT_PAGE_SIZE,
          sorted: DEFAULT_BULK_ACTION_LIST_SORT || [],
          filtered: bulkActionUuid
            ? []
            : filterMap[actionType.value] || [
                ...DEFAULT_BULK_ACTION_FILTER,
                { id: "priorityAgent", value: false },
              ] ||
              [],
          resized: [],
          expanded: {},
        }
  );

  const resetListState = () => {
    listState.pageSize = DEFAULT_PAGE_SIZE;
    listState.page = 0;
    listState.sorted = DEFAULT_BULK_ACTION_LIST_SORT || [];
    listState.filtered = bulkActionUuid
      ? []
      : filterMap[actionType.value] || DEFAULT_BULK_ACTION_FILTER || [];
    listState.resized = [];
    listState.expanded = {};
    updateData(listState, false);
  };

  const getSwitchesForBulkAction = (bulkActionUuid, checked) => {
    const { filtered, sorted } = listState;
    if (checked) {
      sendAuthenticatedAsyncRequest(
        `/bulk-action/${bulkActionUuid}/select-all-switches`, // Updated endpoint
        "POST",
        { filtered, sorted },
        (response) => {
          setSelectedSwitches(response.data);
          setToggleAllSwitches(checked);
          selectAllRecords(response.data, checked); // Mark all as selected.
        },
        (error) => {
          console.error("Error fetching switches:", error);
          setSelectedSwitches([]);
          setToggleAllSwitches(false);
          selectAllRecords([], false);
        }
      );
    } else {
      setSelectedSwitches([]);
      setToggleAllSwitches(false);
      selectAllRecords([], false);
    }
  };

  const toggleAllEditMode = (e) => {
    const { checked } = e.target;
    if (checked) {
      getSwitchesForBulkAction(bulkActionUuid, true);
    } else {
      getSwitchesForBulkAction(bulkActionUuid, false);
    }
  };

  useEffect(() => {
    if (bulkActionUuid !== undefined) {
      getSwitchesForBulkAction(bulkActionUuid, true);
    }
  }, [bulkActionUuid]);

  const getSwitchesForSelectAll = (e) => {
    const { checked } = e.target;
    const { filtered, sorted } = listState;
    if (!bulkActionUuid && checked) {
      let filteredFinal = [...filtered];
      if (selectedCustomerNameAbbreivation.length > 0) {
        filteredFinal.push({
          id: "customerNameAbbreviation",
          value: selectedCustomerNameAbbreivation,
          isdefault: true,
        });
      }
      if (selectedClientNameAbbreviation.length > 0) {
        filteredFinal.push({
          id: "clientNameAbbreviation",
          value: selectedClientNameAbbreviation,
          isdefault: true,
        });
      }
      sendRequestForSelectAll(filteredFinal, sorted, checked);
    } else {
      setSelectedSwitches([]);
      setToggleAllSwitches(checked);
      selectAllRecords([], checked);
    }
  };

  const sendRequestForSelectAll = (filtered, sorted, checked) => {
    sendAuthenticatedAsyncRequest(
      `/swim/get-switches-for-select-all`, // review this api call
      "POST",
      { filtered, sorted },
      (r) => {
        setSelectedSwitches(r.data);
        setToggleAllSwitches(checked);
        selectAllRecords(r.data, checked);
      },
      (e) => {
        setSelectedSwitches([]);
        setToggleAllSwitches(checked);
        selectAllRecords([], checked);
      }
    );
  };

  const toggleAll = () => {
    if (selected.length > 0) {
      let selectedSwictches = [...selected];
      let isAllSelected = selectAll();
      data.forEach((item) => {
        const { wfItemId } = item;
        const index = selectedSwictches.indexOf(wfItemId);
        if (isAllSelected) {
          selectedSwictches.splice(index, 1);
        } else if (index < 0) {
          selectedSwictches.push(wfItemId);
        }
      });
      setSelectedSwitches(selected);
    }
  };
  useEffect(() => {
    if (
      actionType.value === "Client Pause" &&
      (selectedClientNameAbbreviation.length > 0 ||
        selectedCustomerNameAbbreivation.length > 0) &&
      data !== undefined
    ) {
      const { sorted } = listState;
      setDisableTable(true);
      let filtered = [...DEFAULT_BULK_ACTION_CLIENT_PAUSE_FILTER];
      if (selectedClientNameAbbreviation.length > 0) {
        filtered.push({
          id: "clientNameAbbreviation",
          value: selectedClientNameAbbreviation,
          isdefault: true,
        });
      }
      if (selectedCustomerNameAbbreivation.length > 0) {
        filtered.push({
          id: "customerNameAbbreviation",
          value: selectedCustomerNameAbbreivation,
          isdefault: true,
        });
      }
      sendRequestForSelectAll(filtered, sorted, true);
    } else {
      resetListState();
    }
  }, [actionType]);

  const selectAll = () => {
    let isAllSelected = true;
    data.forEach((x) => {
      if (selected.indexOf(x.wfItemId) < 0) {
        isAllSelected = false;
        return;
      }
    });
    return isAllSelected;
  };

  const selectAllRecords = (wfItemIds, checked) => {
    toggleAll();
    setSelectedSwitches(wfItemIds);
    setIsSelectAll(checked);
  };
  const updateData = (state, preventUpdate) => {
    if (!preventUpdate) {
      state.filtered = state.filtered.filter((element) => {
        // Check if value is an array or a string and has a length > 0
        if (typeof element.value === "string" || Array.isArray(element.value)) {
          return element.value.length > 0;
        }
        // Keep the element if the value is a boolean (like true/false)
        if (typeof element.value === "boolean") {
          return true; // For example, keep if it's true
        }
        // Return true for any other type if needed, or add more conditions as required
        return element.value !== undefined && element.value !== null;
      });
      if (state.sorted !== DEFAULT_BULK_ACTION_LIST_SORT) {
        state.sorted.forEach((element, i) => {
          element.isDefault = false;
        });
      }
      const clientFilterIndex = state.filtered.findIndex(
        (element) => element.id === "clientNameAbbreviation"
      );
      const customerFilterIndex = state.filtered.findIndex(
        (element) => element.id === "customerNameAbbreviation"
      );
      const bulkActionItemStatus = state.filtered.findIndex(
        (element) => element.id === "bulkActionItemStatus"
      );
      const bulkActionStatus = state.filtered.findIndex(
        (element) => element.id === "status"
      );
      if (bulkActionStatus !== -1) {
        // Validate status filter for restricted action types before normalization
        if (hasRestrictedStatusFilter(actionType)) {
          const restrictedFilter = getRestrictedStatusFilter(actionType);
          const allowedStatuses = restrictedFilter?.find(
            (filter) => filter.id === "status"
          )?.value || [];
          
          // Normalize and filter statuses
          const currentStatusValues = state.filtered[bulkActionStatus].value || [];
          const normalizedStatuses = currentStatusValues.map((value) => (value.name ? value.name : value));
          
          // Filter out any statuses that are not in the allowed list
          const validStatuses = normalizedStatuses.filter((status) =>
            allowedStatuses.includes(status)
          );
          
          // Update with only valid statuses
          state.filtered[bulkActionStatus].value = validStatuses;
        } else {
          // Normal behavior for non-restricted action types
          state.filtered[bulkActionStatus].value = state.filtered[
            bulkActionStatus
          ].value.map((value) => (value.name ? value.name : value));
        }
      }
      if (bulkActionItemStatus !== -1) {
        state.filtered[bulkActionItemStatus].value = state.filtered[
          bulkActionItemStatus
        ].value.map((value) => (value.name ? value.name : value));
      }

      if (
        clientFilterIndex === -1 &&
        selectedClientNameAbbreviation.length > 0
      ) {
        state.filtered.push({
          id: "clientNameAbbreviation",
          value: selectedClientNameAbbreviation,
          isdefault: true,
        });
      }
      if (
        customerFilterIndex === -1 &&
        selectedCustomerNameAbbreivation &&
        selectedCustomerNameAbbreivation.length > 0
      ) {
        state.filtered.push({
          id: "customerNameAbbreviation",
          value: selectedCustomerNameAbbreivation,
          isdefault: true,
        });
      }
      if (bulkActionUuid && !isFetchedOnce) {
        state.filtered = [];
        setIsFetchedOnce(true);
      }
      dispatch(
        updateInterventionList(
          state.pageSize,
          state.page,
          state.sorted,
          state.filtered,
          state.expanded,
          state.resized,
          bulkActionUuid
        )
      );
    } else {
      dispatch(updateBulkActionListState(state));
    }
  };

  useEffect(() => {
    if (!bulkActionUuid) {
      let defualtListState = {};
      if (actionType.value === "Client Pause") {
        defualtListState = {
          page: 0,
          pageSize: DEFAULT_PAGE_SIZE,
          sorted: DEFAULT_BULK_ACTION_LIST_SORT,
          filtered: [
            ...DEFAULT_BULK_ACTION_CLIENT_PAUSE_FILTER,
            { id: "priorityAgent", value: false },
          ],
          resized: [],
          expanded: {},
        };
      } else {
        defualtListState = {
          page: 0,
          pageSize: DEFAULT_PAGE_SIZE,
          sorted: DEFAULT_BULK_ACTION_LIST_SORT,
          filtered: filterMap[actionType.value] || [
            ...DEFAULT_BULK_ACTION_FILTER,
            { id: "priorityAgent", value: false },
          ],
          resized: [],
          expanded: {},
        };
      }
      dispatch(updateBulkActionListState(defualtListState));
    } else {
      const defualtListState = {
        page: 0,
        pageSize: DEFAULT_PAGE_SIZE,
        sorted: DEFAULT_BULK_ACTION_LIST_SORT,
        filtered: [
          DEFAULT_BULK_ACTION_FILTER,
          { id: "priorityAgent", value: false },
        ],
        resized: [],
        expanded: {},
      };
      dispatch(updateBulkActionListState(defualtListState));
    }
  }, [actionType, bulkActionUuid]);

  const toggleSelection = (selected) => {
    selected = Object.keys(selected).map((row, index) => {
      return row;
    });
    setSelectedSwitches(selected);
  };
  const fetchBioSimilarSwitches = (e) => {
    let { filtered, sorted } = listState; // Extract filtered and sorted from the current state
    if (e.target.checked) {
      setBioSimilarSwitchesToggle(true);
      filtered = filtered.filter((element) => element.id !== "priorityAgent");
      filtered.push({
        id: "priorityAgent",
        value: true,
      });
      updateData({ ...listState, filtered, sorted });
    } else {
      setBioSimilarSwitchesToggle(false);
      filtered = filtered.filter((element) => element.id !== "priorityAgent");
      filtered.push({
        id: "priorityAgent",
        value: false,
      });
      updateData({ ...listState, filtered, sorted });
    }
  };

  const getSwitchesByToggle = (e) => {
    const { checked } = e.target;
    setToggleDefaultFilters(checked);
    const { filtered } = listState;
    let updatedFiltered = [...filtered];
    if (e.target.checked) {
      updatedFiltered = updatedFiltered.filter(
        (element) => element.id !== "state" && element.id !== "status"
      );
      updatedFiltered.push(
        ...(filterMap[actionType.value] || [...DEFAULT_BULK_ACTION_FILTER] ||
          [])
      );
    } else {
      updatedFiltered = updatedFiltered.filter(
        (element) => element.id !== "state" && element.id !== "status"
      );
    }

    const index = listState.filtered.findIndex(
      (element) => element.id === "switchNumber"
    );
    if (index !== -1) {
      listState.filtered[index].operator = SWITCH_NUMBER_OP;
      listState.filtered[index].id = SWITCH_NUMBER_STRING;
    }
    updateData({
      ...listState,
      filtered: updatedFiltered,
    });
  };

  return (
    !disableTable && (
      <Col md={{ span: 13 }} style={{ paddingTop: "10px" }}>
        <div style={{ paddingBottom: "10px" }}>
          <Card variant="primary">
            <Card.Body>
              <Row style={{ flexDirection: "column" }}>
                <Col>
                  {selectedClientNameAbbreviation.length > 0 &&
                    selectedClientNameAbbreviation[0] === "WMT" &&
                    isLoggedInUserBioSimilarUser() &&
                    showBioSimilarToggle && (
                      <Form.Check
                        type="switch"
                        id="bioSimilarSwitchesToggle"
                        label="Bio Similar Switches"
                        onChange={fetchBioSimilarSwitches}
                        style={{
                          display: "inline",
                          paddingRight: "5px",
                          paddingBottom: "10px",
                        }}
                        checked={bioSimilarSwitchesToggle}
                        disabled={isViewable}
                      />
                    )}
                </Col>
                <Col>
                  <Form.Check
                    type="switch"
                    id="selectAll"
                    label="Select All Switches"
                    onChange={
                      bulkActionUuid
                        ? toggleAllEditMode
                        : getSwitchesForSelectAll
                    }
                    style={{ display: "inline", paddingRight: "5px" }}
                    checked={toggleAllSwitches}
                    disabled={isViewable}
                  />
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip>
                        This will select all the switches from all pages
                      </Tooltip>
                    }
                  >
                    {({ ref, ...triggerHandler }) => (
                      <span ref={ref} {...triggerHandler}>
                        <MdHelp
                          style={{ color: "#FFAA3B", cursor: "pointer" }}
                        ></MdHelp>
                      </span>
                    )}
                  </OverlayTrigger>
                  {!shouldHideDefaultFiltersToggle(actionType) && (
                    <>
                      <Form.Check
                        type="switch"
                        id="defaultFilters"
                        label="Default Filters"
                        onChange={getSwitchesByToggle}
                        style={{
                          display: "inline",
                          paddingRight: "5px",
                          marginLeft: "30px",
                        }}
                        checked={toggleDefaultFilters}
                      ></Form.Check>
                      <OverlayTrigger
                        placement="right"
                        overlay={
                          <Tooltip>
                            This will remove all the default filters
                          </Tooltip>
                        }
                      >
                        {({ ref, ...triggerHandler }) => (
                          <span ref={ref} {...triggerHandler}>
                            <MdHelp
                              style={{ color: "#FFAA3B", cursor: "pointer" }}
                            />
                          </span>
                        )}
                      </OverlayTrigger>
                    </>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
          <SwitchListCommon
            updateInterventionList={updateInterventionList}
            updateIndexOnList={updateIndexOnList}
            resetError={resetError}
            data={data}
            pages={pages}
            totalElements={totalElements}
            loading={loading}
            listState={listState}
            sorted={listState.sorted}
            filters={listState.filters}
            isError={isError}
            updateData={updateData}
            columnList={
              bulkActionUuid !== undefined
                ? columnListWithUuid
                : columnListWithoutUuidBasedOnSelectedAction(actionType)
            }
            selectedClient={selectedClientNameAbbreviation}
            isSortFilterEnabledForCust={false}
            toggleSelection={toggleSelection}
            isSelectAll={isSelectAll}
            selected={selected}
            resetTableSelection={resetTableSelection}
            isViewable={isViewable}
            isInitialLoadComplete={true}
            actionType={actionType}
          />
        </div>
      </Col>
    )
  );
};
export default withRouter(BulkActionList);
